import isNative from '../../native/isNative';

const COMPRESSION_THRESHOLD = 100 * 1024; // 100KB

export type CompressionResult = {
  stringifiedJson: string;
  compressedBody: BodyInit | null;
  shouldCompress: boolean;
  compressionType: string;
};

let compressionLevel = 6;
try {
  compressionLevel = parseInt(sessionStorage.getItem('_uaCL') ?? compressionLevel.toString(), 10);
} catch {
  // do nothing
}

export async function compressJsonData(data: unknown): Promise<CompressionResult> {
  let stringifiedJson = '';
  let compressedBody: BodyInit | null = null;
  let shouldCompress = false;
  const compressionType = 'gzip';

  if (typeof data === 'object' && data !== null && !(data instanceof FormData)) {
    try {
      stringifiedJson = JSON.stringify(data);
      if (
        compressionLevel >= 0 &&
        isCompressionStreamAvailable &&
        !isNative() &&
        stringifiedJson.length > COMPRESSION_THRESHOLD
      ) {
        const beforeCompress = Date.now();
        const compressed = await compressBody(stringifiedJson);
        compressedBody = compressed as BodyInit;
        shouldCompress = true;
        console.debug(
          `[compression] From ${Math.round(stringifiedJson.length / 1024)}kb to ${Math.round(compressed.length / 1024)}kb in ${Date.now() - beforeCompress}ms`,
        );
      }
    } catch (error) {
      console.warn('Failed to compress request body:', error);
    }
  }

  return {
    compressionType,
    stringifiedJson,
    compressedBody,
    shouldCompress,
  };
}

export const isCompressionStreamAvailable =
  typeof CompressionStream !== 'undefined' && process.env.NEXT_RUNTIME !== 'edge';

export async function compressBody(body: string): Promise<Uint8Array> {
  // Reach CompressionStream via globalThis so Turbopack's edge-runtime static check
  // doesn't flag the bare global. Runtime never calls this on edge (see isCompressionStreamAvailable).
  const stream = new globalThis.CompressionStream('gzip');
  const writer = stream.writable.getWriter();
  const reader = stream.readable.getReader();

  // Write the body to the compression stream
  const encoder = new TextEncoder();
  const encoded = encoder.encode(body);
  void writer.write(encoded);
  void writer.close();

  // Read the compressed data
  const chunks: Uint8Array[] = [];
  let totalLength = 0;

  const readChunks = async (): Promise<void> => {
    const { value, done } = await reader.read();
    if (done) {
      return;
    }
    if (value) {
      chunks.push(value);
      totalLength += value.length;
    }
    return readChunks();
  };

  await readChunks();

  // Combine all chunks into a single Uint8Array
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }

  return result;
}
